import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import { AppConfig } from '@/datasources/AppConfig';
import { Failure } from '@/usecases/repository';
import { encryptToken, decryptToken, encryptId, decryptId } from '@/utils/tokenCrypto';
import { autenticacaoService } from '@/services/autenticacaoService';
import { OUVIDORIA_ENDPOINTS } from '@/config/ouvidoria';

interface OuvidoriaCreationData {
  nome: string;
  cnpj?: string;
  razaoSocial?: string;
  naturezaJuridica?: string;
  atividadePrincipal?: string;
  associacaoSupervisora?: boolean;
  nomeFantasia?: string;
  email?: string;
  telefone?: string;
  cidade?: string; // Nome do município (obrigatório segundo erro da API)
  geral?: boolean;
  etiquetas?: string[] | null;
  logoFile?: File; // Required for ouvidoria-geral endpoint
  endereco?: {
    logradouro: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
    pais: string;
    ibge?: string;
    latitude?: number;
    longitude?: number;
  };
  [key: string]: unknown;
}

interface UserCreationData {
  id: string; // email or phone
  nome: string;
}

interface OuvidoriaCreationResponse {
  id: string;
  nome: string;
  cnpj: string;
  agentes?: Array<{
    id: string;
    nome: string;
  }>;
}

interface UserCreationResponse {
  tokenAcesso: string;
  tokenAtualizacao: string;
  expira: string;
  recuperarConta?: {
    chave: string;
    segredo: string;
  };
}

interface DadosAgenteGestor {
  nome: string;
  email: string;
  telefone: string;
}

interface CriarOuvidoriaEAgenteResult {
  ouvidoriaId: string;
  usuarioId: string;
  tokenAcesso: string;
  email: string;
}

class OuvidoriaCreationService {
  private readonly urbNetDataSource: RemoteDataSource;
  private readonly authDataSource: RemoteDataSource;

  constructor() {
    this.urbNetDataSource = new RemoteDataSource(Settings.urlBase);
    this.authDataSource = new RemoteDataSource(Settings.urlSecondary);
  }

  /**
   * Creates a new ouvidoria geral (general ouvidoria) with logo upload
   */
  async criarOuvidoriaGeral(
    municipioId: string,
    data: OuvidoriaCreationData,
    tokenId?: string | null
  ): Promise<{ success: OuvidoriaCreationResponse } | { failure: Failure }> {
    // O endpoint ouvidoria-geral espera application/json conforme o schema
    // Request.Ouvidorias.AdicionarOuvidoria (enviar FormData causava HTTP 415).
    // tokenId é passado como query param quando disponível (fluxo de criação unificado).
    const endpointBase = `v1/municipios/${municipioId}/ouvidorias/ouvidoria-geral`;
    const endpoint = tokenId ? `${endpointBase}?tokenId=${encodeURIComponent(tokenId)}` : endpointBase;
    const body: Record<string, unknown> = {
      nome: data.nome,
      // CNPJ apenas com números (remove . / - e demais não dígitos)
      cnpj: (data.cnpj || '').replace(/\D/g, ''),
      // Coordenadas geocodificadas quando disponíveis, senão valores padrão
      latitude: data.endereco?.latitude ?? 0,
      longitude: data.endereco?.longitude ?? 0,
      altitude: 0,
    };

    // Campos opcionais (nullable no schema)
    if (data.razaoSocial) body.razaoSocial = data.razaoSocial;
    if (data.naturezaJuridica) body.naturezaJuridica = data.naturezaJuridica;
    if (data.atividadePrincipal) body.atividadePrincipal = data.atividadePrincipal;
    if (data.associacaoSupervisora === true) body.associacaoSupervisora = true;
    if (data.nomeFantasia) body.nomeFantasia = data.nomeFantasia;
    body.email = data.email || null;
    body.telefone = data.telefone || null;

    // etiquetas: array com minItems 1 e nullable — envia null quando vazio
    body.etiquetas =
      data.etiquetas && Array.isArray(data.etiquetas) && data.etiquetas.length > 0
        ? data.etiquetas
        : null;

    // endereco enviado como objeto (schema Endereco)
    if (data.endereco) {
      body.endereco = {
        logradouro: data.endereco.logradouro,
        numero: data.endereco.numero,
        complemento: data.endereco.complemento || null,
        bairro: data.endereco.bairro,
        cidade: data.endereco.cidade,
        estado: data.endereco.estado,
        cep: data.endereco.cep,
        pais: data.endereco.pais,
        ibge: data.endereco.ibge || null,
      };
    }

    const response = await this.urbNetDataSource.post<OuvidoriaCreationResponse>({
      endpoint,
      body,
      requiredToken: false,
      headers: { 'accept': 'application/vnd.data.full.v1+json' },
    });

    if ('failure' in response) {
      return response;
    }

    return { success: response.success.body as OuvidoriaCreationResponse };
  }

  /**
   * Gera um token de criação de ouvidoria geral (POST /v1/ouvidorias-gerais/tokens).
   * Retorna o id (guid) do token gerado.
   */
  async gerarTokenCriacaoOuvidoriaGeral(): Promise<{ success: { id: string } } | { failure: Failure }> {
    const response = await this.urbNetDataSource.post<{ id: string }>({
      endpoint: OUVIDORIA_ENDPOINTS.gerarTokenOuvidoriaGeral,
      body: {},
      requiredToken: false,
      headers: { 'accept': 'application/vnd.data.full.v1+json' },
    });

    if ('failure' in response) {
      return response;
    }

    return { success: response.success.body as { id: string } };
  }

  /**
   * Fluxo unificado: cria ouvidoria geral + usuário + agente gestor em sequência.
   * Retorna os dados necessários para navegação direta à página de 2FA.
   */
  async criarOuvidoriaGeralEAgente(
    municipioId: string,
    municipioLong: number,
    ouvidoriaData: OuvidoriaCreationData,
    tokenId: string | null,
    dadosAgente: DadosAgenteGestor
  ): Promise<{ success: CriarOuvidoriaEAgenteResult } | { failure: Failure }> {
    // 1) Criar ouvidoria geral (com tokenId)
    const ouvidoriaResult = await this.criarOuvidoriaGeral(municipioId, ouvidoriaData, tokenId);
    if ('failure' in ouvidoriaResult) {
      return ouvidoriaResult;
    }
    const ouvidoriaId = ouvidoriaResult.success.id;

    // Atualiza AppConfig com os dados da ouvidoria criada
    AppConfig.ouvidoriaId = ouvidoriaId;
    AppConfig.municipioId = municipioId;
    AppConfig.municipioLong = municipioLong;

    try {
      const [ouvidoriaIdCifrado, municipioIdCifrado, municipioLongCifrado] = await Promise.all([
        encryptId(ouvidoriaId),
        encryptId(municipioId),
        encryptId(String(municipioLong)),
      ]);
      localStorage.setItem('ouvidoriaId', ouvidoriaIdCifrado);
      localStorage.setItem('municipioId', municipioIdCifrado);
      localStorage.setItem('municipioLong', municipioLongCifrado);
    } catch {
      // Error saving to cookieStorage
    }

    // 2) Criar usuário na API de autenticação
    const usuarioResult = await autenticacaoService.criarUsuario({
      id: dadosAgente.email,
      nome: dadosAgente.nome,
    });

    if ('failure' in usuarioResult) {
      return { failure: new Failure({ messages: [usuarioResult.failure.message] }) };
    }

    const usuarioId = usuarioResult.success.usuario?.id ?? '';
    const tokenAcesso = usuarioResult.success.tokenAcesso;

    if (!usuarioId || !tokenAcesso) {
      return { failure: new Failure({ messages: ['Resposta incompleta ao criar usuário'] }) };
    }

    // 3) Criar agente vinculado à ouvidoria (autenticado com tokenAcesso do novo usuário)
    const formData = new FormData();
    formData.append('nome', dadosAgente.nome);
    if (dadosAgente.email) formData.append('email', dadosAgente.email);
    if (dadosAgente.telefone) formData.append('telefone', dadosAgente.telefone);

    const agenteResult = await this.urbNetDataSource.post({
      endpoint: `v1/ouvidorias/${ouvidoriaId}/agentes`,
      requiredToken: false,
      headers: {
        'Accept': 'application/force+json',
        'Authorization': `bearer ${tokenAcesso}`,
      },
      body: formData as unknown as Record<string, unknown>,
    });

    if ('failure' in agenteResult) {
      return agenteResult;
    }

    // Extrai agenteId da resposta para salvar no AppConfig
    type CriacaoAgenteResponse = { id?: string; agenteId?: string };
    const agenteBody = agenteResult.success.body as CriacaoAgenteResponse | undefined;
    const agenteId = agenteBody?.id ?? agenteBody?.agenteId ?? '';

    if (agenteId) {
      AppConfig.agenteId = agenteId;
      try {
        const agenteIdCifrado = await encryptId(agenteId);
        localStorage.setItem('agenteId', agenteIdCifrado);
      } catch {
        // Error saving agenteId
      }
    }

    return { success: { ouvidoriaId, usuarioId, tokenAcesso, email: dadosAgente.email } };
  }

  /**
   * Creates a new ouvidoria and extracts the IDs from the response
   */
  async criarOuvidoria(
    municipioId: string,
    data: OuvidoriaCreationData
  ): Promise<{ success: OuvidoriaCreationResponse } | { failure: Failure }> {
    // O endpoint espera application/json conforme o schema
    // Request.Ouvidorias.AdicionarOuvidoria (enviar FormData causa HTTP 415).
    // Hierarquia: `associacaoSupervisora` marca a ouvidoria como supervisora
    // (exige natureza "Associação Privada" + atividade de defesa de direitos
    // sociais); a supervisora efetiva é resolvida pelo backend.
    const body: Record<string, unknown> = {
      nome: data.nome,
      cnpj: (data.cnpj || '').replace(/\D/g, ''),
      latitude: data.endereco?.latitude ?? 0,
      longitude: data.endereco?.longitude ?? 0,
      altitude: 0,
    };

    // Campos opcionais (nullable no schema)
    if (data.razaoSocial) body.razaoSocial = data.razaoSocial;
    if (data.naturezaJuridica) body.naturezaJuridica = data.naturezaJuridica;
    if (data.atividadePrincipal) body.atividadePrincipal = data.atividadePrincipal;
    body.associacaoSupervisora = data.associacaoSupervisora === true;
    if (data.nomeFantasia) body.nomeFantasia = data.nomeFantasia;
    body.email = data.email || null;
    body.telefone = data.telefone || null;

    // etiquetas: minItems 1 e nullable — envia null quando vazio
    body.etiquetas =
      data.etiquetas && Array.isArray(data.etiquetas) && data.etiquetas.length > 0
        ? data.etiquetas
        : null;

    // endereco enviado como objeto (schema Endereco)
    if (data.endereco) {
      body.endereco = {
        logradouro: data.endereco.logradouro,
        numero: data.endereco.numero,
        complemento: data.endereco.complemento || null,
        bairro: data.endereco.bairro,
        cidade: data.endereco.cidade,
        estado: data.endereco.estado,
        cep: data.endereco.cep,
        pais: data.endereco.pais,
        ibge: data.endereco.ibge || null,
      };
    }

    const response = await this.urbNetDataSource.post<OuvidoriaCreationResponse>({
      endpoint: `v1/municipios/${municipioId}/ouvidorias`,
      body,
      requiredToken: true,
      headers: { 'accept': 'application/vnd.data.full.v1+json' },
    });

    if ('failure' in response) {
      return response;
    }

    return { success: response.success.body as OuvidoriaCreationResponse };
  }

  /**
   * Creates a new user and extracts the tokens from the response
   */
  async criarUsuario(
    data: UserCreationData
  ): Promise<{ success: UserCreationResponse } | { failure: Failure }> {
    const response = await this.authDataSource.post<UserCreationResponse>({
      // urlSecondary já inclui o segmento /autenticacao na base
      endpoint: 'v1/usuarios',
      body: data as unknown as Record<string, unknown>,
      requiredToken: false,
    });

    if ('failure' in response) {
      return response;
    }

    return { success: response.success.body as UserCreationResponse };
  }

  /**
   * Cria uma ouvidoria sem alterar a sess\u00e3o atual (AppConfig). Usado por um gestor
   * j\u00e1 autenticado para criar uma nova ouvidoria setorial supervisionada,
   * mantendo intactos `AppConfig.ouvidoriaId`, `municipioId`, `agenteId`, token, etc.
   *
   * Retorna o id da ouvidoria criada para que a UI o repasse adiante (cadastro
   * de agente etc.) sem precisar gravar no estado global.
   */
  async criarOuvidoriaParaSupervisao(
    municipioId: string,
    data: OuvidoriaCreationData
  ): Promise<{ success: { ouvidoriaId: string } } | { failure: Failure }> {
    // Use ouvidoria-geral endpoint if geral=true
    const isGeral = data.geral === true;
    const ouvidoriaResult = isGeral
      ? await this.criarOuvidoriaGeral(municipioId, data)
      : await this.criarOuvidoria(municipioId, data);

    if ('failure' in ouvidoriaResult) {
      return ouvidoriaResult;
    }
    return { success: { ouvidoriaId: ouvidoriaResult.success.id } };
  }

  /**
   * Creates a new ouvidoria and saves the ID to AppConfig and cookieStorage
   */
  async criarOuvidoriaESalvarId(
    municipioId: string,
    municipioLong: number,
    data: OuvidoriaCreationData
  ): Promise<{ success: { ouvidoriaId: string } } | { failure: Failure }> {
    // Step 1: Create ouvidoria
    // Use ouvidoria-geral endpoint if geral=true
    const isGeral = data.geral === true;
    const ouvidoriaResult = isGeral
      ? await this.criarOuvidoriaGeral(municipioId, data)
      : await this.criarOuvidoria(municipioId, data);

    if ('failure' in ouvidoriaResult) {
      return ouvidoriaResult;
    }

    const ouvidoriaResponse = ouvidoriaResult.success;

    // Extract ID from ouvidoria response
    const ouvidoriaId = ouvidoriaResponse.id;

    // Update AppConfig with ouvidoria data
    AppConfig.ouvidoriaId = ouvidoriaId;
    AppConfig.municipioId = municipioId;
    AppConfig.municipioLong = municipioLong;

    // Persist to cookieStorage (encrypted)
    try {
      const [ouvidoriaIdCifrado, municipioIdCifrado, municipioLongCifrado] = await Promise.all([
        encryptId(ouvidoriaId),
        encryptId(municipioId),
        encryptId(String(municipioLong)),
      ]);
      localStorage.setItem('ouvidoriaId', ouvidoriaIdCifrado);
      localStorage.setItem('municipioId', municipioIdCifrado);
      localStorage.setItem('municipioLong', municipioLongCifrado);
    } catch (error) {
      // Error saving to cookieStorage
    }

    return { success: { ouvidoriaId } };
  }

  /**
   * Main flow: Creates ouvidoria and user, then updates AppConfig with the returned data
   */
  async criarOuvidoriaEUsuario(
    municipioId: string,
    ouvidoriaData: OuvidoriaCreationData,
    userData: UserCreationData
  ): Promise<{ success: boolean } | { failure: Failure }> {
    // Step 1: Create ouvidoria
    // Use ouvidoria-geral endpoint if geral=true
    const isGeral = ouvidoriaData.geral === true;
    const ouvidoriaResult = isGeral
      ? await this.criarOuvidoriaGeral(municipioId, ouvidoriaData)
      : await this.criarOuvidoria(municipioId, ouvidoriaData);

    if ('failure' in ouvidoriaResult) {
      return ouvidoriaResult;
    }

    const ouvidoriaResponse = ouvidoriaResult.success;

    // Extract IDs from ouvidoria response
    const ouvidoriaId = ouvidoriaResponse.id;
    const agenteId = ouvidoriaResponse.agentes && ouvidoriaResponse.agentes.length > 0 
      ? ouvidoriaResponse.agentes[0].id 
      : '';

    // Update AppConfig with ouvidoria data
    AppConfig.ouvidoriaId = ouvidoriaId;
    AppConfig.municipioId = municipioId;
    AppConfig.agenteId = agenteId;
    // Note: municipioLong should be set separately as it comes from the municipio data

    // Step 2: Create user
    const userResult = await this.criarUsuario(userData);
    
    if ('failure' in userResult) {
      return userResult;
    }

    const userResponse = userResult.success;

    // Extract tokens from user response
    const tokenAcesso = userResponse.tokenAcesso;
    const refreshToken = userResponse.tokenAtualizacao;
    const dataExpiracao = userResponse.expira;
    const chaveRecuperacao = userResponse.recuperarConta?.chave;
    const segredoRecuperacao = userResponse.recuperarConta?.segredo;

    // Update AppConfig with token data
    AppConfig.tokenAcesso = tokenAcesso;
    AppConfig.refreshToken = refreshToken;
    AppConfig.dataExpiracao = dataExpiracao;
    AppConfig.chaveRecuperacao = chaveRecuperacao;
    AppConfig.segredoRecuperacao = segredoRecuperacao;
    AppConfig.token = { accessToken: tokenAcesso };

    // Step 3: Persist to cookieStorage (equivalent to SharedPreferences in Dart)
    await this.persistToLocalStorage({
      tokenAcesso,
      refreshToken,
      dataExpiracao,
      chaveRecuperacao,
      segredoRecuperacao,
      ouvidoriaId,
      municipioId,
      agenteId,
    });

    return { success: true };
  }

  /**
   * Persists configuration data to cookieStorage
   */
  private async persistToLocalStorage(data: {
    tokenAcesso: string;
    refreshToken: string;
    dataExpiracao: string;
    chaveRecuperacao?: string;
    segredoRecuperacao?: string;
    ouvidoriaId: string;
    municipioId: string;
    agenteId: string;
  }): Promise<void> {
    try {
      // Tokens encriptados (AES-GCM) antes de gravar em disco
      const [tokenAcessoCifrado, refreshTokenCifrado, ouvidoriaIdCifrado, municipioIdCifrado, agenteIdCifrado] = await Promise.all([
        encryptToken(data.tokenAcesso),
        encryptToken(data.refreshToken),
        encryptId(data.ouvidoriaId),
        encryptId(data.municipioId),
        encryptId(data.agenteId),
      ]);
      localStorage.setItem('tokenAcesso', tokenAcessoCifrado);
      localStorage.setItem('refreshToken', refreshTokenCifrado);
      localStorage.setItem('dataExpiracao', data.dataExpiracao);

      if (data.chaveRecuperacao) {
        localStorage.setItem('chaveRecuperacao', data.chaveRecuperacao);
      }

      if (data.segredoRecuperacao) {
        localStorage.setItem('segredoRecuperacao', data.segredoRecuperacao);
      }

      localStorage.setItem('ouvidoriaId', ouvidoriaIdCifrado);
      localStorage.setItem('municipioId', municipioIdCifrado);
      localStorage.setItem('agenteId', agenteIdCifrado);
    } catch (error) {
      // Error persisting to cookieStorage
    }
  }

  /**
   * Loads configuration data from cookieStorage
   */
  static async loadFromLocalStorage(): Promise<void> {
    try {
      const tokenAcessoCifrado = localStorage.getItem('tokenAcesso');
      const refreshTokenCifrado = localStorage.getItem('refreshToken');
      const dataExpiracao = localStorage.getItem('dataExpiracao');
      const chaveRecuperacao = localStorage.getItem('chaveRecuperacao');
      const segredoRecuperacao = localStorage.getItem('segredoRecuperacao');
      const ouvidoriaIdCifrado = localStorage.getItem('ouvidoriaId');
      const municipioIdCifrado = localStorage.getItem('municipioId');
      const agenteIdCifrado = localStorage.getItem('agenteId');

      // Tokens e IDs vêm encriptados do disco; decripta para uso em memória
      const [tokenAcesso, refreshToken, ouvidoriaId, municipioId, agenteId] = await Promise.all([
        tokenAcessoCifrado ? decryptToken(tokenAcessoCifrado) : Promise.resolve(''),
        refreshTokenCifrado ? decryptToken(refreshTokenCifrado) : Promise.resolve(''),
        ouvidoriaIdCifrado ? decryptId(ouvidoriaIdCifrado) : Promise.resolve(''),
        municipioIdCifrado ? decryptId(municipioIdCifrado) : Promise.resolve(''),
        agenteIdCifrado ? decryptId(agenteIdCifrado) : Promise.resolve(''),
      ]);

      if (tokenAcesso) AppConfig.tokenAcesso = tokenAcesso;
      if (refreshToken) AppConfig.refreshToken = refreshToken;
      if (dataExpiracao) AppConfig.dataExpiracao = dataExpiracao;
      if (chaveRecuperacao) AppConfig.chaveRecuperacao = chaveRecuperacao;
      if (segredoRecuperacao) AppConfig.segredoRecuperacao = segredoRecuperacao;
      if (ouvidoriaId) AppConfig.ouvidoriaId = ouvidoriaId;
      if (municipioId) AppConfig.municipioId = municipioId;
      if (agenteId) AppConfig.agenteId = agenteId;

      if (tokenAcesso) {
        AppConfig.token = { accessToken: tokenAcesso };
      }
    } catch (error) {
      // Error loading from cookieStorage
    }
  }
}

export { OuvidoriaCreationService };
export type { OuvidoriaCreationData, UserCreationData, DadosAgenteGestor, CriarOuvidoriaEAgenteResult };
